import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const withMelbi = [
  "Licensed clinician review",
  "Patient-specific prescription when appropriate",
  "Licensed U.S. pharmacy fulfillment",
  "Clear labeling and instructions",
  "Access to message the care team",
  "Refill review for continued appropriateness",
];

const grayMarket = [
  "No required prescription",
  "No clinician review",
  "Unclear ingredient source",
  "Unclear concentration or dosing",
  "Limited quality documentation",
  "No pharmacy accountability",
  "No provider support",
];

const melbiDifference = [
  "Licensed clinician review",
  "Structured online intake",
  "Patient-specific prescriptions when appropriate",
  "Licensed U.S. pharmacy fulfillment",
  "Clear instructions and labeling",
  "Access to message the care team",
  "Refill review for continued appropriateness",
  "No automatic approvals",
  "No gray-market sourcing",
  "No research-use-only products",
];

function SectionHeading({ children, testId }) {
  return (
    <h2
      data-testid={testId}
      className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29] leading-tight"
    >
      {children}
    </h2>
  );
}

function Para({ children }) {
  return (
    <p className="mt-3 text-[#5E5C58] leading-relaxed">{children}</p>
  );
}

export default function TrustYourSource() {
  useSEO({
    title: "Trust Your Source | Licensed Clinicians & U.S. Pharmacy | Melbi",
    description:
      "Not all wellness sources are the same. Melbi uses licensed clinician review, U.S. pharmacy fulfillment, and clear labeling — no shortcuts.",
    url: "https://melbiwellness.com/trust-your-source",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Trust Your Source", url: "/trust-your-source" },
      ]),
    ],
  });
  return (
    <div data-testid="trust-your-source-page" className="bg-[#FAF6EF]">
      {/* ARTICLE HEADER */}
      <section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Trust your source
          </p>
          <h1
            data-testid="trust-your-source-h1"
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C2B29] leading-[1.1]"
          >
            Before You Inject It:{" "}
            <span className="italic text-[#C96A4E]">Why the Source Matters</span>
          </h1>
          <p
            data-testid="trust-your-source-subhead"
            className="mt-4 md:mt-5 text-[#5E5C58] text-base md:text-lg leading-relaxed"
          >
            What to know about licensed pharmacy fulfillment, clinical oversight,
            and the risks of gray-market sourcing.
          </p>
        </div>
      </section>

      {/* ARTICLE BODY */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-7 md:pt-10 pb-10 md:pb-14 space-y-10 md:space-y-12">
        {/* Intro */}
        <section>
          <p className="text-[#5E5C58] leading-relaxed">
            Shopping for injectable wellness treatments online can get confusing fast.
          </p>
          <p className="mt-4 text-[#5E5C58] leading-relaxed">
            A quick search can turn up everything from licensed telehealth
            providers to websites selling injectable products without a
            prescription. The difference is not always obvious at first
            glance&mdash;and when a product is going into your body, it matters.
          </p>
          <p
            data-testid="trust-source-pullquote"
            className="mt-10 md:mt-12 font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-[#2C2B29] leading-tight border-l-4 border-[#C96A4E] pl-5 md:pl-6"
          >
            When something is being injected into your body, the{" "}
            <span className="italic text-[#C96A4E]">source should never be a mystery.</span>
          </p>
          <p className="mt-8 md:mt-10 text-[#5E5C58] leading-relaxed">
            You deserve to know what you&apos;re putting in your body, where it
            came from, and whether it was prepared through a legitimate pharmacy
            process.
          </p>
        </section>

        {/* Gray market */}
        <section>
          <SectionHeading testId="section-gray-market">
            The risk with gray-market products
          </SectionHeading>
          <Para>
            Gray-market and black-market injectable products may look legitimate
            online, but patients often have no clear way to verify what they are
            getting.
          </Para>
          <Para>
            The product may not come from a licensed pharmacy. It may not require
            a prescription. It may be labeled for &quot;research use only.&quot;
            The strength, concentration, purity, sterility, or actual ingredient
            may be unclear.
          </Para>
          <Para>
            Patients may also be left to figure out dosing on their own through
            social media, online forums, or instructions from sellers who are
            not licensed clinicians.
          </Para>
          <Para>
            <em>That is not guided care. That is a guessing game.</em>
          </Para>
        </section>

        {/* 503A */}
        <section>
          <SectionHeading testId="section-503a">
            What a 503A compounding pharmacy means
          </SectionHeading>
          <Para>
            A 503A compounding pharmacy is a licensed pharmacy that prepares
            medications for individual patients based on patient-specific
            prescriptions.
          </Para>
          <Para>
            In simple terms: a licensed clinician reviews the patient first. If
            treatment is appropriate, the clinician issues a prescription for
            that specific patient. The pharmacy then compounds and dispenses the
            medication according to that prescription.
          </Para>
          <Para>
            This is different from buying a vial online from a gray-market
            website, private seller, or research-use-only source.
          </Para>
          <Para>
            A licensed 503A compounding pharmacy follows applicable pharmacy
            laws, labeling requirements, documentation practices, and quality
            standards, including sterile compounding standards when required.
            While compounded medications are not FDA-approved, they are
            prepared through a legitimate pharmacy process tied to a real
            patient prescription. That is a very different path from purchasing
            an injectable product from an unverified or research-use-only
            source online.
          </Para>
        </section>

        {/* Comparison card */}
        <section
          data-testid="comparison-card"
          className="bg-white border border-[#E8E5DF] rounded-3xl p-5 md:p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Side by side
          </p>
          <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29] leading-tight">
            Melbi vs. gray-market sourcing
          </h3>
          <div className="mt-6 grid md:grid-cols-2 gap-4 md:gap-5">
            <div className="rounded-2xl border border-[#E8B5A1]/60 bg-[#FBEEE5] p-4 md:p-5">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C96A4E]">
                With Melbi
              </p>
              <ul className="mt-3 space-y-2.5 text-sm text-[#2C2B29]">
                {withMelbi.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0">
                      <Check size={13} className="text-[#C96A4E]" />
                    </span>
                    <span className="leading-snug">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-[#E8E5DF] bg-[#F3F1EC]/60 p-4 md:p-5">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8A9A92]">
                With gray-market sourcing
              </p>
              <ul className="mt-3 space-y-2.5 text-sm text-[#5E5C58]">
                {grayMarket.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0">
                      <X size={13} className="text-[#8A9A92]" />
                    </span>
                    <span className="leading-snug">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pharmacy Path */}
        <section>
          <SectionHeading>The pharmacy matters more than you may think</SectionHeading>
          <Para>
            With injectable wellness products, the cheapest option is not always
            the clearest option.
          </Para>
          <Para>
            You deserve to know what you&apos;re putting in your body, who
            prepared it, whether it was prescribed specifically for you, and how
            to use it correctly.
          </Para>
          <Para>
            That is where the pharmacy process matters. Melbi works with
            licensed U.S. pharmacy partners expected to follow applicable
            pharmacy standards, sourcing practices, labeling requirements, and
            quality processes. When applicable, legitimate pharmacy partners
            should be able to provide documentation about the medication and
            how it was prepared.
          </Para>
          <Para>
            Gray-market sourcing often leaves too many questions unanswered.
            Melbi was built to make the path clearer from intake, to clinician
            review, to pharmacy fulfillment.
          </Para>
        </section>

        {/* Clinical review */}
        <section>
          <SectionHeading>Why clinical review matters</SectionHeading>
          <Para>
            Injectable wellness treatments are not right for everyone.
          </Para>
          <Para>
            Your health history, medications, allergies, pregnancy status,
            medical conditions, and prior reactions can all affect whether a
            treatment is appropriate. That is why Melbi does not use an
            automatic approval model.
          </Para>
          <Para>
            Every patient completes a structured intake. A licensed clinician
            reviews the information and can approve, deny, request more details,
            or escalate to a brief live visit when needed.
          </Para>
        </section>

        {/* Dosing */}
        <section>
          <SectionHeading>Dosing should not be DIY</SectionHeading>
          <Para>
            One of the biggest problems with gray-market injectable products is
            dosing confusion.
          </Para>
          <Para>
            Different vial sizes. Different concentrations. Unclear
            instructions. No clinician oversight. No one to ask.
          </Para>
          <Para>
            With Melbi, dosing is based on the prescription and the specific
            product dispensed by the pharmacy. Patients receive clear
            instructions and have access to message the care team with
            questions along the way.
          </Para>
          <Para>
            You should not have to guess what you are injecting or how to use it.
          </Para>
        </section>

        {/* The Melbi Difference list */}
        <section>
          <SectionHeading>The Melbi difference</SectionHeading>
          <Para>
            Melbi was built for patients who want convenient access without
            cutting corners.
          </Para>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm md:text-base text-[#2C2B29]">
            {melbiDifference.map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#F3F1EC] flex items-center justify-center shrink-0">
                  <Check size={13} className="text-[#C96A4E]" />
                </span>
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Bottom Line */}
        <section>
          <SectionHeading>The bottom line</SectionHeading>
          <Para>
            When it comes to injectable wellness treatments, source matters.
          </Para>
          <Para>
            Patients deserve to know where their medication comes from, who
            reviewed their health history, how it was prescribed, how to use
            it, and who to contact with questions.
          </Para>
          <Para>
            That is why Melbi was built around clinician review,
            patient-specific prescriptions, licensed pharmacy fulfillment, and
            ongoing support from the start.
          </Para>
        </section>

        {/* Disclaimer */}
        <p
          data-testid="trust-your-source-disclaimer"
          className="text-xs md:text-sm text-[#8A9A92] leading-relaxed border-t border-[#E8E5DF] pt-6"
        >
          Compounded medications are not FDA-approved and are prescribed only
          when clinically appropriate. Not all patients qualify, and individual
          results vary.
        </p>
      </article>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5E6D3] via-[#F3F1EC] to-[#E8D5C4]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C2B29] leading-[1.1]">
            Care you can{" "}
            <span className="italic text-[#C96A4E]">trust.</span>
          </h2>
          <p className="mt-4 text-[#5E5C58] text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Clinician guided wellness, fulfilled by a licensed pharmacy partner.
          </p>
          <button
            type="button"
            data-testid="trust-your-source-start-now-cta"
            onClick={goToIntake}
            className="mt-7 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-3.5 text-sm font-semibold hover:bg-[#B35A40] transition-all shadow-[0_8px_24px_rgba(201,106,78,0.25)] hover:shadow-[0_12px_32px_rgba(201,106,78,0.35)] hover:-translate-y-0.5"
          >
            Start now
            <ArrowRight size={16} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
}
